/**
 * Admin → Audit Sink (T1.4, 2026-05-28)
 *
 * Configure a SECONDARY destination for audit events. The DB chain
 * (audit_chain_blocks, RLS-isolated per tenant) is always the
 * PRIMARY persistence; this surface adds an optional customer-
 * owned SIEM/webhook/log-aggregator endpoint.
 *
 * Built-in kinds:
 *   - local_only - no secondary destination (default)
 *   - webhook    - HTTPS POST + HMAC-SHA256 signature (Standard Webhooks
 *                  pattern); body format: json | cef | leef
 *
 * Backend: GET / PUT / POST-test at /v1/admin/audit-sink.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Save,
  Send,
  Shield,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  getAuditSinkConfig,
  upsertAuditSinkConfig,
  testAuditSink,
  type AuditSinkAdminState,
  type AuditSinkTestResult,
} from '@/lib/api';

type SinkKind = 'local_only' | 'webhook';

export function AdminAuditSink(): JSX.Element {
  const [state, setState] = useState<AuditSinkAdminState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [kind, setKind] = useState<SinkKind>('local_only');
  const [enabled, setEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookFormat, setWebhookFormat] = useState<'json' | 'cef' | 'leef'>('json');
  const [showSecret, setShowSecret] = useState(false);

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<AuditSinkTestResult | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await getAuditSinkConfig();
      setState(r);
      // Hydrate form from persisted config (when present)
      if (r.config) {
        setKind((r.config.kind as SinkKind) ?? 'local_only');
        setEnabled(r.config.enabled);
        const cfg = r.config.config ?? {};
        setWebhookUrl(typeof cfg.url === 'string' ? cfg.url : '');
        setWebhookSecret(typeof cfg.secret === 'string' ? cfg.secret : '');
        setWebhookFormat(
          cfg.format === 'cef' || cfg.format === 'leef' ? cfg.format : 'json',
        );
      } else {
        // No row = default local_only / disabled
        setKind('local_only');
        setEnabled(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load audit-sink config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function buildPersistConfig(): Record<string, unknown> {
    if (kind === 'webhook') {
      return { url: webhookUrl.trim(), secret: webhookSecret, format: webhookFormat };
    }
    return {};
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    setTestResult(null);
    try {
      await upsertAuditSinkConfig({ kind, config: buildPersistConfig(), enabled });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to save audit-sink config');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest(): Promise<void> {
    setTesting(true);
    setTestResult(null);
    try {
      // Test the in-form config WITHOUT saving - operator can iterate
      // until green, then click Save.
      const r = await testAuditSink({ kind, config: buildPersistConfig() });
      setTestResult(r);
    } catch (e) {
      setTestResult({
        ok: false,
        kind,
        error: e instanceof Error ? e.message : 'test failed',
        health: { kind, reachable: false, lastSuccessAt: null },
      });
    } finally {
      setTesting(false);
    }
  }

  if (loading && !state) {
    return (
      <PageContainer>
        <PageHeader title="Audit Sink" subtitle="Secondary SIEM / webhook destination" />
        <LoadingState caption="Loading audit-sink config…" />
      </PageContainer>
    );
  }

  if (error && !state) {
    return (
      <PageContainer>
        <PageHeader title="Audit Sink" subtitle="Secondary SIEM / webhook destination" />
        <ErrorState
          title="Couldn't load audit-sink config"
          detail={error}
          onRetry={refresh}
          severity="critical"
        />
      </PageContainer>
    );
  }

  const formDirty =
    !state?.config ||
    state.config.kind !== kind ||
    state.config.enabled !== enabled ||
    (kind === 'webhook' &&
      ((state.config.config?.url as string | undefined) !== webhookUrl ||
        (state.config.config?.secret as string | undefined) !== webhookSecret ||
        (state.config.config?.format as string | undefined) !== webhookFormat));

  return (
    <PageContainer>
      <PageHeader
        title="Audit Sink"
        subtitle="Secondary destination for audit events. The DB chain (audit_chain_blocks) stays the primary persistence - this surface optionally also ships every event to a customer-owned SIEM / webhook / log aggregator for cross-system correlation."
        actions={
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
        }
      />

      {error && state ? (
        <GlassCard className="mb-4 border-destructive/30">
          <div className="p-4 flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </GlassCard>
      ) : null}

      {/* Current status banner */}
      <GlassCard className="mb-6">
        <div className="p-5 flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <div className="flex-1">
            <div className="font-medium">
              Current state:{' '}
              <Badge variant={state?.config?.enabled ? 'success' : 'glass'}>
                {state?.config?.enabled ? 'enabled' : 'disabled'}
              </Badge>{' '}
              <Badge variant="outline">{state?.config?.kind ?? 'local_only'}</Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Last successful emit:{' '}
              {state?.config?.lastUsedAt
                ? new Date(state.config.lastUsedAt).toLocaleString()
                : 'never'}
            </div>
          </div>
        </div>
      </GlassCard>

      <GlassCard>
        <CardHeader>
          <CardTitle>Configure</CardTitle>
          <CardDescription>
            Pick a kind. Registered kinds:{' '}
            <code className="text-xs">{(state?.registeredKinds ?? []).join(', ')}</code>
          </CardDescription>
        </CardHeader>
        <div className="px-6 pb-6 space-y-5">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Sink kind</label>
            <select
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={kind}
              onChange={(e) => setKind(e.target.value as SinkKind)}
            >
              <option value="local_only">local_only - no secondary destination (default)</option>
              <option value="webhook">webhook - HTTPS POST + HMAC signature</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="audit-sink-enabled"
              type="checkbox"
              className="h-4 w-4"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <label htmlFor="audit-sink-enabled" className="text-sm">
              Enable sink (when unchecked, dispatch is skipped even if a kind is selected)
            </label>
          </div>

          {kind === 'webhook' ? (
            <>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Webhook URL (https recommended)
                </label>
                <input
                  type="url"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                  placeholder="https://your-siem.example/foctta-audit-webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  HMAC secret (used for signature header verification on your side)
                </label>
                <div className="flex gap-2">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    className="flex-1 rounded-md border bg-background px-3 py-2 text-sm font-mono"
                    placeholder="32+ char random string"
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSecret((v) => !v)}
                    title={showSecret ? 'Hide secret' : 'Reveal secret'}
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Verify by computing <code>HMAC-SHA256(secret, body)</code> and comparing to
                  the <code>X-FOCTTA-Signature</code> header.
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Body format</label>
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={webhookFormat}
                  onChange={(e) =>
                    setWebhookFormat(e.target.value as 'json' | 'cef' | 'leef')
                  }
                >
                  <option value="json">JSON - full event envelope (recommended)</option>
                  <option value="cef">CEF - ArcSight CEF:0 line (Splunk / Sentinel / QRadar / ESM)</option>
                  <option value="leef">LEEF - IBM QRadar LEEF:2.0 line</option>
                </select>
              </div>
            </>
          ) : null}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || (kind === 'webhook' && (!webhookUrl.trim() || !webhookSecret))}
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send test event
                </>
              )}
            </Button>
            <Button onClick={handleSave} disabled={saving || !formDirty}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save config
                </>
              )}
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* Test result */}
      {testResult ? (
        <GlassCard
          className={`mt-4 ${
            testResult.ok ? 'border-success/40' : 'border-destructive/40'
          }`}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {testResult.ok ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : (
                <AlertCircle className="h-5 w-5 text-destructive" />
              )}
              Test result
            </CardTitle>
          </CardHeader>
          <div className="px-6 pb-6 space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">kind:</span>{' '}
              <code>{testResult.kind}</code>
            </div>
            {testResult.elapsedMs !== undefined ? (
              <div>
                <span className="text-muted-foreground">elapsed:</span>{' '}
                {testResult.elapsedMs} ms
              </div>
            ) : null}
            {testResult.message ? (
              <div className="text-muted-foreground">{testResult.message}</div>
            ) : null}
            {testResult.error ? (
              <div className="text-destructive">
                Error: <code>{testResult.error}</code>
              </div>
            ) : null}
            <div>
              <span className="text-muted-foreground">health:</span>{' '}
              <Badge variant={testResult.health.reachable ? 'success' : 'critical'}>
                {testResult.health.reachable ? 'reachable' : 'unreachable'}
              </Badge>
            </div>
            {testResult.health.lastError ? (
              <div className="text-xs text-destructive">
                Last error: {testResult.health.lastError}
              </div>
            ) : null}
          </div>
        </GlassCard>
      ) : null}
    </PageContainer>
  );
}

export default AdminAuditSink;
